import { isDesktop } from '@/consts/device';
import getFile from '@/requests/cloud-storage/getFile';
import { getAppStore, setAppStore } from '@/store';
import { LightboxSource } from '@/store/lightbox';
import { RightPanelTab } from '@/store/navigation/types';
import { driveFileByIdToContentFile } from '@/utils/media/driveFileToContentFile';
import { Screen } from '@/types';

import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';

type DriveFileDeeplink = Extract<Deeplink, { kind: DeeplinkKind.DriveFile }>;

/**
 * `file_uid` deeplink: open a generation straight from its Drive file.
 *
 * The sibling of `taskHandler`, one step shorter. A `task_id` names a
 * Magic-Flow job, so that handler resolves the job first and only then reads
 * the Drive files behind its results; a `file_uid` names the stored result
 * outright, so there is no job to resolve — which also means it keeps working
 * for a generation old enough that its task has aged out of the flow API.
 *
 * Routes to the same place by the same store writes, so a result opened either
 * way lands in an identical lightbox — the app lightbox, opened on this file
 * alone, independent of the creations list. It shares
 * `driveFileToContentFile`'s attribute reading and media-type logic, so an
 * entry arrived at by deeplink is indistinguishable from the same entry
 * scrolled to in the list.
 *
 * `resultFile` is deliberately not set: that carries a `FlowMedia` (the job's
 * output descriptor), which a Drive file alone cannot reconstruct.
 */
const driveFileHandler = async ({
  fileUid,
}: DriveFileDeeplink): Promise<void> => {
  setAppStore({ screen: Screen.Loading, hasAutoSelectedPreset: true });

  try {
    const file = await getFile({ uid: fileUid });
    if (!file) {
      setAppStore({ screen: Screen.Library, hasAutoSelectedPreset: false });
      return;
    }

    setAppStore({
      screen: Screen.Files,
      ...(isDesktop && { rightPanelTab: RightPanelTab.Creations }),
      hasAutoSelectedPreset: false,
    });
    getAppStore().openLightbox({
      items: [driveFileByIdToContentFile(file)],
      source: LightboxSource.Deeplink,
      resultMode: true,
    });
  } catch {
    setAppStore({ screen: Screen.Library, hasAutoSelectedPreset: false });
  }
};

export default driveFileHandler;
